import React, { useEffect, useRef } from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import BenefitsFeature from "./benefitFeatures.js";
import "./benefits.css";

import decentralizedImage from "../../../../static/Images/benefits/decenterlized.png";
import fairImage from "../../../../static/Images/benefits/fair.png";
import globalImage from "../../../../static/Images/benefits/global-access.png";
import payoutsImage from "../../../../static/Images/benefits/instant-payouts.png";
import gamesImage from "../../../../static/Images/benefits/wide-game-selection.png";
import communityImage from "../../../../static/Images/benefits/community-driven.png";

import { setupBenefitsAnimation } from "../animations";

const benefitsFeatures = [
  {
    title: "Decentralized Gaming",
    description: "Play with freedom, transparency, and full control over your gaming experience.",
    image: decentralizedImage,
  },
  {
    title: "Provable Fairness",
    description: "Verify game results with transparent technology and trust every outcome.",
    image: fairImage,
  },
  {
    title: "Global Access",
    description: "Play anywhere, anytime, and connect with players from around the world.",
    image: globalImage,
  },
  {
    title: "Instant Payouts",
    description: "Get your winnings directly in your wallet quickly, securely, and with ease.",
    image: payoutsImage,
  },
  {
    title: "Diverse Games",
    description: "Explore lotteries, slots, original games, and a variety of exciting experiences.",
    image: gamesImage,
  },
  {
    title: "Player Community",
    description: "Shape the platform through a connected, engaging, and player-driven community.",
    image: communityImage,
  },
];

const Benefits = () => {
  const theme = useTheme();
  const sectionRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const cleanup = setupBenefitsAnimation(sectionRef, introRef);
    return cleanup;
  }, []);

  return (
    <Box
      id="benefits"
      component="section"
      className="home-benefits"
      ref={sectionRef}
      sx={(theme) => ({
        width: "100%",
        pt: {
          xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
          sm: theme.custom.layoutSizes.section.paddingTop,
        },
        pb: {
          xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
          sm: theme.custom.layoutSizes.section.paddingBottom,
        },
        px: {
          ...theme.custom["home-section-left-padding"],
          xs: theme.custom.layoutSizes.mobileStandard.padding.leftRight,
        },
        position: "relative",
      })}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Intro */}
        <Box
          ref={introRef}
          sx={{
            width: "100%",
            height: "auto !important",
            zIndex: 50,
          }}
        >
          <Box 
            className="intro-bg-wrapper" 
            sx={{ width: "100%", backgroundColor: "#ffffff" }}
          >
            <Box
              className="benefits-intro"
              sx={(theme) => ({
                ...(theme.typography.homeSection?.headerContainer || {}),
                mb: (theme) => ({
                  xs: theme.custom.layoutSizes.mobileStandard.spacing.textGroupBottomMargin,
                  sm: theme.spacing(5),
                }),
              })}
            >
            <Typography
              component="p"
              className="benefits-kicker"
              sx={{
                ...(theme.typography.homeSection?.eyebrow || {}),
                fontSize: {
                  xs: theme.custom.layoutSizes.mobileStandard.typography.eyebrow.fontSize,
                },
                marginBottom: {
                  xs: theme.custom.layoutSizes.mobileStandard.typography.eyebrow.marginBottom,
                },
                color: theme.custom.colors.home.brandRed,
                "&::before": {
                  ...(theme.typography.homeSection?.eyebrow?.["&::before"] || {}),
                  background: theme.custom.colors.home.brandRed,
                },
              }}
            >
              BUILT BY PLAYERS. POWERED BY BLOCKCHAIN.
            </Typography>

            <Typography
              component="h2"
              className="benefits-title"
              sx={{
                ...(theme.typography.homeSection?.heading || {}),
                color: theme.custom.colors.home.navyDark,
              }}
            >
              Why AutoBET?
            </Typography>

            <Typography
              component="p"
              className="benefits-description"
              sx={{
                ...(theme.typography.homeSection?.description || {}),
                fontSize: {
                  xs: theme.custom.layoutSizes.mobileStandard.typography.paragraph.fontSize,
                },
                color: "text.secondary",
              }}
            >
              Explore the features that make AutoBET transparent,
              accessible, and built for a global community of players.
            </Typography>
            </Box>
          </Box>
        </Box>

        {/* Benefits Cards */}
        <Box className="benefits-container" sx={{ width: "100%", boxSizing: "border-box" }}>
          <Box className="benefits-cards-wrapper">
            <Box className="benefits-cards">
              {benefitsFeatures.map((feature, index) => (
                <BenefitsFeature
                  key={feature.title}
                  number={index + 1}
                  title={feature.title}
                  description={feature.description}
                  image={feature.image}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Benefits;